import { lazy, LazyExoticComponent, ComponentType } from 'react';
import { getCountryCode } from 'typescript/utilities/locales';

/**
 * IMPORTANT
 * 
 * This function is used to fetch a localized component based on the country code.
 * It will attempt to load the component with the country-specific file extension first,
 * and if that fails, it will fall back to the base component file.
 * 
 * In order to keep file naming consistent, the fallback file should be
 * configured for the US locale and all other locales should have their own
 * file with the country code as the extension.
 * 
 * e.g. 
 *
 * components/ComponentName.tsx - default fallback (US)
 * 
 * components/ComponentName.fr.tsx - FR specific
 * 
 * @param filepath - the base path of the component file
  This should be the path without any locale-specific suffix.
 * @param overrides - an optional object that maps country codes to their respective file extensions.
  this allows for things like if two countries can share the same file
 * @returns localized component
 */
const fetchLocalizedComponent = (
  filepath: string,
  overrides: { [key: string]: string } = {},
): LazyExoticComponent<ComponentType<any>> => {
  const storeCode = getCountryCode()?.toLowerCase();
  const fileExt = overrides[storeCode] || storeCode;
  const localizedFilepath = `${filepath}.${fileExt}`;

  return lazy(() =>
    import(
      /* webpackExclude: /\.test/ */ `components/${localizedFilepath}`
    ).catch(
      () => import(/* webpackExclude: /\.test/ */ `components/${filepath}`),
    ),
  );
};

export { fetchLocalizedComponent };
